import lodash from 'lodash';
import moment from 'moment';

import styles from './File.scss';
import { DEFAULT_DATETIME_INPUT_FORMAT } from 'common/util/dateTime';
import { connect } from 'react-redux';
import { Component } from 'react';
import { push } from 'react-router-redux';
import QuickMenu from 'common/components/QuickMenu/QuickMenu';
import { documentDownloadRequest, deleteUnfiledDocument } from 'part11/actions/documents';
import { buildDocumentActions, buildDocumentTypePermissions, documentFailedConversion } from 'common/permissions/helper';
import { manageTasksSelector } from 'common/permissions/selectors';
import DataType from 'complionAdmin/components/BinderTemplateQuery/DataType/DataType';

class File extends Component {


    constructor(props) {
        super(props);
        // Used to identify where user coming from, so we can set appropriate "Go Back"
        this.fromQueryValue = 'documentList';
    }

    gotoFile() {
        const { document, isUnfiledDocuments, push, binderId, studyId } = this.props;
        const { id } = document;

        const path = studyId ? 'study' : 'binder';
        const pathId = studyId ? studyId : binderId;
        let url = '';

        if (isUnfiledDocuments) {
            url = `/${path}/${pathId}/files/${id}/initialIndex`;
        } else {
            url = `/${path}/${pathId}/files/${id}`;
        }

        if (studyId) {
            url += '?from=' + this.fromQueryValue;
        }

        push(url);
    }

    gotoSigningPage() {
        const { document, push, binderId, studyId } = this.props;
        if (studyId) {
            push(`/study/${studyId}/files/${document.id}/sign?from=${this.fromQueryValue}`);
        } else {
            push(`/binder/${binderId}/files/${document.id}/sign`);
        }
    }

    getDocumentActions() {
        const {
            document,
            binderId,
            studyId,
            myDocumentTypes,
            documentDownloadRequest,
            push,
            deleteUnfiledDocument,
            canManageIntegrationStatuses
        } = this.props;

        return buildDocumentActions(
            document,
            binderId,
            myDocumentTypes,
            {
                downloadRequest: documentDownloadRequest,
                push: push,
                signDocument: this.gotoSigningPage.bind(this),
                sendDocumentForAction: this.gotoSendDocumentForActionForm.bind(this),
                deleteUnfiledDocument,
            },
            canManageIntegrationStatuses,
            { studyId, from: this.fromQueryValue }
        );
    }

    getDocumentTypePermissions() {
        const { myDocumentTypes } = this.props;
        const { documentType } = this.props.document;
        return buildDocumentTypePermissions(documentType, myDocumentTypes);
    }

    gotoSendDocumentForActionForm() {
        const { push, document, binderId, studyId } = this.props;
        const location = {
            pathname: '/form/process/sdfa',
            query: {
                docid: document.id,
                iid: document.institutionId,
                ...(studyId && { studyId }),
            },
            hash: `#${binderId}`
        };
        push(location);
    }

    render() {
        const { document, tasks, canManageTasks, expiration } = this.props;

        if (lodash.isNil(document)) {
            return null;
        }

        const { name, uploadUser, documentType, uploadDate } = document;

        const options = this.getDocumentActions();
        const documentPermissions = this.getDocumentTypePermissions();
        // CRI-1215 filter out files that user has no permissions
        if (!documentPermissions.viewPDF) {
            return ('');
        }
        const conversionFailed = documentFailedConversion(document.conversionStatus);
        const sdfaEnabled = documentPermissions.sdfa && !conversionFailed;
        const documentTypeValue = documentType && documentType.id ? documentType.displayName : null;
        const uploadUserName = uploadUser && uploadUser.displayName;
        const uploadUserEmail = uploadUser && uploadUser.emailAddress;

        return (
            <li className={styles.file} onClick={this.gotoFile.bind(this)} data-cy="File-render">
                <div className={styles.fileHeader}>
                    <h3 className={styles.fileName} title={name} data-cy="File-name">{ conversionFailed && <span>Conversion Issue</span> }{name}</h3>
                    <div className={styles.fileHeaderStatus} data-cy="File-fileHeaderStatus">
                        <FileSignatures tasks={tasks} sdfaEnabled={sdfaEnabled} enabled={canManageTasks}/>
                    </div>
                </div>
                <FileSideSection options={options} />
                <label className={styles.fileCornerLabel} data-cy="File-documentTypeValue">{documentTypeValue}</label>
                <ul className={styles.fileAttributeSection} data-cy="File-fileAttributeSection">
                    <FileAttribute label={'Upload By'} value={uploadUserName} type={'small_text'}/>
                    <FileAttribute label={'Email Address'} value={uploadUserEmail} type={'small_text'}/>
                    <FileAttribute label={'Upload Date'} value={uploadDate} type={'datetime'}/>
                    { expiration && <FileExpiration expiration={expiration} docid={document.id}/>}
                </ul>
            </li>
        );
    }
}

const FileAttribute = ( { label, value, type } ) => {
    return (
        <li data-cy="File-dataType">
            <label>{label}</label>
            <p><DataType value={value} dataType={type} /></p>
        </li>
    );
};

const FileSideSection = ({ options }) => {
    return (
        <div
            className={styles.quickMenuContainer}
            onClick={e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
            }}
            data-cy="File-documentListItem">
            <QuickMenu headerText='Actions' theme='documentListItem' options={options} />
        </div>
    );
};

const FileExpiration = ( { expiration, docid } ) => {
    const expirationDate = moment(expiration, DEFAULT_DATETIME_INPUT_FORMAT);
    const now = moment();
    const daysExpired = expirationDate.diff(now, 'days');

    if (daysExpired > 0 && daysExpired < 60) {
        const days = lodash.eq(daysExpired, 1) ? 'Day' : 'Days';
        return <h6 className={styles.expiring} data-cy="File-Expiring">Expires in {daysExpired} {days}</h6>;
    } else if (daysExpired < 0) {
        const absDaysExpired = Math.abs(daysExpired);
        const days = lodash.eq(absDaysExpired, 1) ? 'Day' : 'Days';
        return <h6 className={styles.expired} data-cy="File-Expired"> Expired {absDaysExpired} {days} Ago</h6>;
    } else if (daysExpired === 0) {
        return <h6 className={styles.expiring}>Expires Today!</h6>;
    }

    return <h6></h6>;
};

const FileSignatures = ( { tasks, sdfaEnabled, enabled } ) => {

    if (!enabled) {
        return <h6></h6>;
    }

    if (!sdfaEnabled) {
        return <h6 className={styles.signaturesUnavailable} data-cy="File-unavailable">signatures unavailable</h6>;
    }

    if (lodash.isEqual(tasks, {})) {
        return <h6 className={styles.signaturesNotRequested} data-cy="File-requested">no signatures requested</h6>;
    }

    const active = parseInt(tasks.activeInstances);

    if (lodash.gt(active, 0)) {
        const signatures = `signature${lodash.gt(active, 1) ? 's' : ''}`;
        return <h6 className={styles.signaturesOutstanding} data-cy="File-signatures">{active} {signatures} outstanding</h6>;
    }

    return <h6 className={styles.signaturesComplete} data-cy="File-complete">signatures complete</h6>;
};

export default connect(
    (state) => {
        const canManageTasks = manageTasksSelector(state);
        const canManageIntegrationStatuses = lodash.get(state, 'me.canManageIntegrationStatuses');
        return {
            myDocumentTypes: lodash.get(state, 'me.documentTypes'),
            canManageTasks,
            canManageIntegrationStatuses,
        };
    },
    {
        push,
        documentDownloadRequest,
        deleteUnfiledDocument,
    }
)(File);
